import { createReducer, on } from '@ngrx/store';
import * as baseState from '../../../../core/models/base-state/base-state';
import { ProxyUserRequestDuplicate, ProxyUserRequestResponse } from '../../models/proxy-user-request';
import * as proxyAccessActions from '../actions/proxy-access.actions';

export const proxyAccessFeatureKey = 'proxy-access';

export interface ProxyAccessState {
  /**
   * The accepted authorization as the API echoed it back, or null if nothing has been accepted.
   * Doubles as the "was it submitted" flag — one source of truth, so there is no way to be
   * submitted-but-without-a-reference-number.
   */
  response: ProxyUserRequestResponse | null;
  /**
   * AC6: the request already on file when the API rejects this one as a duplicate. Held separately
   * from `response` on purpose — nothing was accepted, so `isSubmitted` stays false and the form does
   * not navigate to the confirmation page claiming a submission that didn't happen.
   */
  duplicate: ProxyUserRequestDuplicate | null;
  baseState: baseState.BaseState;
}

export const initialState: ProxyAccessState = {
  response: null,
  duplicate: null,
  baseState: baseState.createBaseState(undefined),
};

/**
 * The slice resets to `initialState` on an in-account patient switch via
 * `resetPatientStateMetaReducer` — nothing to wire up here.
 */
export const proxyAccessReducer = createReducer(
  initialState,

  // A new attempt clears the previous failure — and the previous duplicate notice, which described
  // the values the user has since changed — so a retry doesn't render either beside a request that is
  // still in flight.
  on(
    proxyAccessActions.submitProxyUserRequest,
    (state): ProxyAccessState => ({
      ...state,
      duplicate: null,
      baseState: { ...state.baseState, isLoading: true, error: null },
    })
  ),

  on(
    proxyAccessActions.submitProxyUserRequestSuccess,
    (state, { response }): ProxyAccessState => ({
      response,
      duplicate: null,
      baseState: { ...state.baseState, isLoading: false, error: null },
    })
  ),

  on(
    proxyAccessActions.submitProxyUserRequestFailure,
    (state, { error }): ProxyAccessState => ({
      ...state,
      response: null,
      duplicate: null,
      baseState: { ...state.baseState, isLoading: false, error },
    })
  ),

  // No `error`: a duplicate is a settled outcome, not a failure to retry, and setting both would
  // render the generic "please try again" alongside the reference number of the request on file.
  on(
    proxyAccessActions.submitProxyUserRequestDuplicate,
    (state, { duplicate }): ProxyAccessState => ({
      ...state,
      response: null,
      duplicate,
      baseState: { ...state.baseState, isLoading: false, error: null },
    })
  ),

  on(proxyAccessActions.resetProxyUserRequest, (): ProxyAccessState => initialState)
);
